import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Grid } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { SublistQuestion } from 'store/types/ApplicationQuestion';
import { ApplicationFileAnswer, SublistAnswer } from 'store/types/ApplicationAnswer';
import Modal from 'components/shared/Modal';
import { ModalProps } from 'store/types/ComponentProps';
import QuestionType from 'store/enums/QuestionType';
import { v4 as uuid } from 'uuid';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import moment from 'moment/moment';
import { getISODateString } from 'util/Format';
import { getQuestionId, getSublistQuestionId } from 'util/Application';
import FileInfo from 'store/types/FileInfo';
import MultilineTextQuestionFormItem from '../../MultilineTextQuestionFormItem';
import SelectQuestionFormItem from '../../SelectQuestionFormItem';
import DropdownCustomListQuestionFormItem from '../../DropdownCustomListQuestionFormItem';
import FileQuestionFormItem from '../../FileQuestionFormItem';
import DateQuestionFormItem from '../../DateQuestionFormItem';
import CurrencyQuestionFormItem from '../CurrencyQuestionFormItem';
import PhoneQuestionFormItem from '../../PhoneQuestionFormItem';
import EmailQuestionFormItem from '../../EmailQuestionFormItem';

import commonStyles from 'styles/common.module.scss';

interface AddSublistItemModalProps extends ModalProps {
  questions: SublistQuestion[];
  fieldName: string;
  parentQuestionType?: QuestionType;
}

const getQuestionInput = (
  fieldName: string,
  referenceKey: string,
  { type, options, required, fileRequired, files }: SublistQuestion,
  open: boolean
) => {
  const inputProps = {
    fieldName,
    options,
    disabled: !open,
    required: required && open,
    fileRequired: fileRequired && open,
    files,
  };

  switch (type) {
    case QuestionType.Text:
      return <MultilineTextQuestionFormItem {...inputProps} />;
    case QuestionType.Currency:
      return <CurrencyQuestionFormItem {...inputProps} />;
    case QuestionType.Date:
      return <DateQuestionFormItem {...inputProps} />;
    case QuestionType.Dropdown:
      return <SelectQuestionFormItem {...inputProps} />;
    case QuestionType.DropdownCustomList:
      return <DropdownCustomListQuestionFormItem {...inputProps} />;
    case QuestionType.File:
      return <FileQuestionFormItem key={referenceKey} {...inputProps} showTable={false} />;
    case QuestionType.Checkbox:
      return <></>;
    case QuestionType.Phone:
      return <PhoneQuestionFormItem {...inputProps} />;
    case QuestionType.Email:
      return <EmailQuestionFormItem {...inputProps} />;
    default:
      return <></>;
  }
};

const getQuestionFieldName = (questionId: string): string => `sublistQuestion-${questionId}`;

const getDefaultViewValues = (questions: SublistQuestion[]) => {
  return {
    ...questions.reduce((acc, { id }) => {
      return { ...acc, [getQuestionFieldName(id)]: '' };
    }, {}),
  };
};

const AddSublistItemModal: React.FunctionComponent<AddSublistItemModalProps> = ({
  onClose,
  open,
  questions,
  fieldName,
}) => {
  const { control, setValue, reset, getValues } = useFormContext();
  const [referenceKey, setReferenceKey] = useState<string>(uuid());
  const controlFieldNameView = useMemo(() => `${fieldName}-view`, [fieldName]);
  const controlFieldNameData = useMemo(() => `${fieldName}-data`, [fieldName]);
  const sublistAnswers: Array<SublistAnswer[]> = useWatch({ control, name: fieldName });
  const sublistAnswersView = useWatch({ control, name: controlFieldNameView });
  const addButtonDisabled: boolean = useMemo(
    () =>
      !sublistAnswersView ||
      questions.filter(({ id, required }) => required && !sublistAnswersView[getQuestionFieldName(id)]).length > 0,
    [questions, sublistAnswersView]
  );

  const generateNewReferenceKey = useCallback(() => {
    setReferenceKey(uuid());
  }, []);

  const getControlName = useCallback(
    (questionId: string) => `${controlFieldNameView}.${getQuestionFieldName(questionId)}`,
    [controlFieldNameView]
  );

  const handleAddItem = useCallback(() => {
    const values = getValues();
    const files = values['files'] || {};
    const existingData = Object.values(values[controlFieldNameData] ?? []);

    const newAnswers = [...existingData];

    const sublistFiles: ApplicationFileAnswer = files ? files[controlFieldNameView] : {};

    Object.entries(sublistFiles || {}).forEach(([key, fileArray]) => {
      if (!fileArray?.length) return;

      const questionId = getQuestionId(fieldName);
      const sublistQuestionId = getSublistQuestionId(key);
      const question = questions.find(({ id }) => id === sublistQuestionId);

      newAnswers.push({
        answer: 'File',
        type: question?.type || QuestionType.File,
        content: question?.content || '',
        sublistQuestionId,
        files: fileArray.map((f: FileInfo) => ({ name: f.name })),
        referenceKey,
      });

      setValue(`sublistFiles.${referenceKey}`, {
        questionId,
        sublistQuestionId,
        files: fileArray || [],
      });

      // TODO: Make sure this clears on add & new
      setValue(`files.${getControlName(sublistQuestionId)}`, []);
    });

    setValue(fieldName, [...(sublistAnswers || []), newAnswers]);
    setValue(controlFieldNameView, getDefaultViewValues(questions));
    setValue(controlFieldNameData, '');

    generateNewReferenceKey();
  }, [
    getValues,
    controlFieldNameData,
    controlFieldNameView,
    setValue,
    fieldName,
    sublistAnswers,
    questions,
    getControlName,
    generateNewReferenceKey,
    referenceKey,
  ]);

  const handleAddItemAndClose = useCallback(() => {
    handleAddItem();
    onClose();
  }, [handleAddItem, onClose]);

  const handleClose = useCallback(() => {
    reset(
      {
        ...getValues(),
        [controlFieldNameView]: '',
        [controlFieldNameData]: '',
      },
      { keepDirty: true }
    );
    onClose();
  }, [controlFieldNameData, controlFieldNameView, getValues, onClose, reset]);

  useEffect(() => {
    if (open) {
      reset({
        ...getValues(),
        [controlFieldNameView]: getDefaultViewValues(questions),
      });
    }
  }, [controlFieldNameData, controlFieldNameView, getValues, open, questions, reset]);

  useEffect(() => {
    if (!sublistAnswersView) return;

    questions.forEach(({ id, type, content }) => {
      const questionValue = sublistAnswersView[getQuestionFieldName(id)];
      if (questionValue) {
        setValue(`${controlFieldNameData}.${id}`, {
          answer: moment.isMoment(questionValue) ? getISODateString(questionValue) || '' : questionValue,
          sublistQuestionId: id,
          type,
          content,
          referenceKey,
        });
      }
    });
  }, [controlFieldNameData, controlFieldNameView, questions, referenceKey, setValue, sublistAnswersView]);

  return (
    <Modal
      onClose={handleClose}
      open={open}
      title={'Add New Item'}
      actions={
        <Grid {...defaultGridContainerProps}>
          <Grid {...defaultGridItemProps} xs={true}>
            <Button color={'secondary'} variant={'outlined'} onClick={handleClose}>
              {'Cancel'}
            </Button>
          </Grid>
          <Grid {...defaultGridItemProps} xs={'auto'}>
            <Button color={'secondary'} variant={'contained'} onClick={handleAddItem} disabled={addButtonDisabled}>
              {'Add & Create New'}
            </Button>
          </Grid>
          <Grid {...defaultGridItemProps} xs={'auto'}>
            <Button
              color={'secondary'}
              variant={'contained'}
              onClick={handleAddItemAndClose}
              disabled={addButtonDisabled}
            >
              {'Add'}
            </Button>
          </Grid>
        </Grid>
      }
    >
      <Grid {...defaultGridContainerProps}>
        {questions.map((question) => (
          <Grid {...defaultGridItemProps} key={question.id}>
            <h5 className={commonStyles.subTitle}>
              {question.content}
              {question.required ? '*' : ''}
            </h5>
            {getQuestionInput(getControlName(question.id), referenceKey, question, open)}
          </Grid>
        ))}
      </Grid>
    </Modal>
  );
};
export default AddSublistItemModal;
